import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Globe } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { SelectionGridSkeleton } from "@/components/loading";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type { Llm } from "@/types/data";

interface LlmPickerProps {
      searchSpecificationId: string;
      className?: string;
}

export function LlmPicker({ searchSpecificationId, className }: LlmPickerProps) {
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const noBatchRequest = { trpc: { context: { noBatch: true } } } as const;

      const updateSearchSpecificationMutation = useMutation(
            trpc.searchSpecifications.updateSearchSpecification.mutationOptions({
                  onSuccess: () => {
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchSpecifications.getSearchSpecificationLlms.queryKey({
                                    id: searchSpecificationId!,
                              }),
                        });
                  },
            }),
      );

      const { data: searchSpecificationLlms, isLoading: isLoadingSearchSpecificationLlms } = useQuery(
            trpc.searchSpecifications.getSearchSpecificationLlms.queryOptions({
                  id: searchSpecificationId!,
            }, noBatchRequest),
      );

      const { data: llms, isLoading: isLoadingLlms } = useQuery(
            trpc.llms.getLlms.queryOptions(undefined, noBatchRequest),
      );
      const [localSelectedLlms, setLocalSelectedLlms] = useState<Llm[]>([]);

      // Handle multiple selection like checkboxes
      const handleLlmSelection = useCallback(
            (llm: Llm) => {
                  const isSelected = localSelectedLlms.some((selected) => selected.id === llm.id);

                  let newSelection: Llm[];
                  if (isSelected) {
                        // Remove if already selected
                        newSelection = localSelectedLlms.filter((selected) => selected.id !== llm.id);
                  } else {
                        // Add if not selected
                        newSelection = [...localSelectedLlms, llm];
                  }

                  setLocalSelectedLlms(newSelection);
                  updateSearchSpecificationMutation.mutate({
                        id: searchSpecificationId!,
                        llmIds: newSelection.map((llm) => llm.id),
                  });
            },
            [localSelectedLlms, searchSpecificationId, updateSearchSpecificationMutation],
      );

      // Update local state when prop changes
      useEffect(() => {
            if (!searchSpecificationLlms) {
                  setLocalSelectedLlms([]);
                  return;
            }

            const selectedLlms: Llm[] = [];
            for (const dbLlm of searchSpecificationLlms) {
                  if (dbLlm?.id) {
                        const foundLlm = llms?.find((llm) => llm.id === dbLlm.id);
                        if (foundLlm) {
                              selectedLlms.push(foundLlm);
                        }
                  }
            }
            setLocalSelectedLlms(selectedLlms);
      }, [searchSpecificationLlms, llms]);

      if (isLoadingSearchSpecificationLlms || isLoadingLlms) {
            return (
                  <SelectionGridSkeleton
                        items={5}
                        className={cn("grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3", className)}
                  />
            );
      }

      return (
            <div className={cn("grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3", className)}>
                  {llms?.map((llm) => {
                        const isSelected = localSelectedLlms.some((selected) => selected.id === llm.id);

                        return (
                              <Card
                                    key={llm.id}
                                    className={cn(
                                          "cursor-pointer transition-all duration-200 relative group p-2",
                                          isSelected
                                                ? "border border-primary bg-primary/10 "
                                                : "border hover:border-muted-foreground/50 hover:bg-muted/50",
                                    )}
                                    onClick={() => handleLlmSelection(llm)}
                              >
                                    {/* Selection indicator */}
                                    {isSelected && (
                                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center ">
                                                <Check className="w-4 h-4 text-primary-foreground" />
                                          </div>
                                    )}

                                    <CardHeader className="relative">
                                          {llm.webSearch && (
                                                <Tooltip>
                                                      <TooltipTrigger asChild>
                                                            <div className="absolute top-0 left-0 rounded-md text-info/60 ">
                                                                  <Globe className="w-4 h-4" />
                                                            </div>
                                                      </TooltipTrigger>
                                                      <TooltipContent>
                                                            <p>This model has internet access</p>
                                                      </TooltipContent>
                                                </Tooltip>
                                          )}
                                          <div className="flex flex-col items-center  gap-3 text-center">
                                                <div className="w-10 h-10 bg-muted rounded-full  flex items-center justify-center">
                                                      <img
                                                            src={llm.logo ?? ""}
                                                            alt={`${llm.provider} logo`}
                                                            className="w-8 h-8 object-contain"
                                                            onError={(e) => {
                                                                  // Fallback to provider name if image fails to load
                                                                  const target = e.target as HTMLImageElement;
                                                                  target.style.display = "none";
                                                                  const fallback =
                                                                        target.nextElementSibling as HTMLElement;
                                                                  if (fallback) fallback.style.display = "block";
                                                            }}
                                                      />
                                                </div>
                                                <CardTitle className="text-sm font-medium">{llm.modelLabel}</CardTitle>
                                          </div>
                                    </CardHeader>
                              </Card>
                        );
                  })}
            </div>
      );
}
